import {
  AfterViewInit,
  Component,
  forwardRef,
  ViewChild,
  ElementRef,
  OnDestroy,
  Injector,
  Input,
  Output,
  EventEmitter,
  HostBinding
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validator, NG_VALIDATORS, AbstractControl, ValidationErrors } from '@angular/forms';

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ]
})
export class PhoneInputComponent implements ControlValueAccessor, Validator, AfterViewInit, OnDestroy {
  @ViewChild('phoneInput') phoneInputRef: ElementRef;

  /** 'default' = 52px fixed (e.g. trip-creator). 'compact' = responsive heights to match add-passenger dialog. */
  @Input() size: 'default' | 'compact' = 'default';
  /** Emits when the inner input blurs (e.g. for saving on blur). */
  @Output() blur = new EventEmitter<void>();
  /** Emits when the inner input is focused (e.g. for showing autocomplete). */
  @Output() focus = new EventEmitter<void>();
  @HostBinding('class.phone-input-compact') get isCompact(): boolean {
    return this.size === 'compact';
  }

  private iti: any;
  private pendingValue: string | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private listener = () => {
    this.enforceDigitsOnly();
    this.emitValue();
  };
  private countryChangeHandler = () => {
    this.enforceDigitsOnly();
    this.emitValue();
    this.triggerValidation();
  };
  private onKeyDown = (e: KeyboardEvent) => {
    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    if (allowed.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };
  private onPaste = (e: ClipboardEvent) => {
    e.preventDefault();
    if (!this.iti) {
      return;
    }
    const pasted = (e.clipboardData && e.clipboardData.getData('text') || '').replace(/\D/g, '');
    if (!pasted) {
      return;
    }
    const country = this.iti.getSelectedCountryData();
    const dial = country.dialCode || '';
    let digits = pasted;
    if (dial && digits.startsWith(dial)) {
      digits = digits.substring(dial.length);
    }
    if (country.iso2 === 'jm') {
      if (digits.startsWith('1') && digits.length > 10) {
        digits = digits.substring(1);
      }
      digits = digits.substring(0, 10);
    }
    this.iti.setNumber(digits ? '+' + dial + digits : '');
    this.emitValue();
  };

  constructor(private injector: Injector) {}

  ngAfterViewInit() {
    const el = this.phoneInputRef && this.phoneInputRef.nativeElement;
    const initFn = (window as any).intlTelInput;
    if (!el || typeof initFn !== 'function') return;

    const base = (document.querySelector('base') && document.querySelector('base').getAttribute('href')) || '/';
    const utilsUrl = base.startsWith('http')
      ? (new URL(base).origin + new URL(base).pathname.replace(/\/$/, '')) + '/assets/js/utils.js'
      : (window.location.origin || '') + (base === '/' ? '' : base.replace(/\/$/, '')) + '/assets/js/utils.js';

    this.iti = initFn(el, {
      preferredCountries: ['jm'],
      initialCountry: 'jm',
      separateDialCode: false,
      nationalMode: false,
      autoInsertDialCode: true,
      utilsScript: utilsUrl
    });

    if (this.pendingValue != null) {
      const pending = this.pendingValue;
      this.pendingValue = null;
      this.setNumberFromDigits(pending);
      this.emitValue();
    }

    el.addEventListener('input', this.listener);
    el.addEventListener('keydown', this.onKeyDown);
    el.addEventListener('paste', this.onPaste);
    el.addEventListener('countrychange', this.countryChangeHandler);
    el.addEventListener('focus', () => this.focus.emit());
    el.addEventListener('blur', () => {
      this.enforceDigitsOnly();
      this.emitValue();
      this.onTouched();
      this.triggerValidation();
      this.blur.emit();
    });
  }

  ngOnDestroy() {
    const el = this.phoneInputRef && this.phoneInputRef.nativeElement;
    if (el) {
      el.removeEventListener('input', this.listener);
      el.removeEventListener('keydown', this.onKeyDown);
      el.removeEventListener('paste', this.onPaste);
      el.removeEventListener('countrychange', this.countryChangeHandler);
    }
    if (this.iti && this.iti.destroy) {
      this.iti.destroy();
    }
  }

  private triggerValidation() {
    try {
      const ngControl = this.injector.get(NgControl, null);
      if (ngControl && ngControl.control) {
        ngControl.control.updateValueAndValidity();
      }
    } catch (e) {
      // NgControl not available when component is used without formControlName
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }
    if (!this.iti) {
      return null;
    }
    const nationalDigits = this.getNationalDigitsOnly();
    const country = this.iti.getSelectedCountryData();
    if (country && country.iso2 === 'jm') {
      return /^\d{10}$/.test(nationalDigits) ? null : { invalidPhone: true };
    }
    try {
      const valid = typeof this.iti.isValidNumberPrecise === 'function'
        ? this.iti.isValidNumberPrecise()
        : (this.iti.isValidNumber ? this.iti.isValidNumber() : null);
      if (valid === null || valid === undefined) {
        return null;
      }
      return valid ? null : { invalidPhone: true };
    } catch (e) {
      return null;
    }
  }

  /** National number as digits only (e.g. Jamaica: 8764335126). */
  private getNationalDigitsOnly(): string {
    if (!this.iti) {
      const el = this.phoneInputRef && this.phoneInputRef.nativeElement;
      return el ? String(el.value || '').replace(/\D/g, '') : '';
    }
    const e164 = this.iti.getNumber() || '';
    if (!e164) {
      const el = this.phoneInputRef.nativeElement;
      let digits = String(el.value || '').replace(/\D/g, '');
      const country = this.iti.getSelectedCountryData();
      const dial = country && country.dialCode ? String(country.dialCode) : '';
      if (dial && digits.startsWith(dial)) {
        digits = digits.substring(dial.length);
      }
      if (country && country.iso2 === 'jm' && digits.startsWith('1') && digits.length > 10) {
        digits = digits.substring(1);
      }
      return digits;
    }
    const country = this.iti.getSelectedCountryData();
    const dialCode = country && country.dialCode ? String(country.dialCode) : '';
    const allDigits = e164.replace(/\D/g, '');
    if (dialCode && allDigits.startsWith(dialCode)) {
      return allDigits.substring(dialCode.length);
    }
    return allDigits;
  }

  private enforceDigitsOnly(): void {
    if (!this.iti) {
      return;
    }
    const country = this.iti.getSelectedCountryData();
    if (!country || country.iso2 !== 'jm') {
      return;
    }
    let digits = this.getNationalDigitsOnly();
    if (digits.startsWith('1') && digits.length > 10) {
      digits = digits.substring(1);
    }
    digits = digits.substring(0, 10);
    const dial = country.dialCode || '1';
    const target = digits ? '+' + dial + digits : '';
    const current = this.iti.getNumber() || '';
    if (target !== current) {
      this.iti.setNumber(target);
    }
  }

  private emitValue() {
    const value = this.getNationalDigitsOnly();
    this.onChange(value || null);
  }

  private setNumberFromDigits(digits: string): void {
    if (!this.iti) {
      return;
    }
    if (!digits) {
      this.iti.setNumber('');
      return;
    }
    const country = this.iti.getSelectedCountryData();
    const dial = (country && country.dialCode) || '1';
    let national = digits;
    if (national.startsWith(dial)) {
      national = national.substring(dial.length);
    }
    if (country && country.iso2 === 'jm' && national.startsWith('1') && national.length > 10) {
      national = national.substring(1);
    }
    this.iti.setNumber('+' + dial + national);
  }

  writeValue(value: any): void {
    const str = value != null ? String(value).trim() : '';
    const digits = str.replace(/\D/g, '');
    if (this.iti) {
      this.setNumberFromDigits(digits);
    } else {
      this.pendingValue = digits || null;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    const el = this.phoneInputRef && this.phoneInputRef.nativeElement;
    if (el) {
      el.disabled = isDisabled;
    }
  }

  /** Blur the inner input (e.g. after selecting from autocomplete so next focus fires). */
  blurInput(): void {
    const el = this.phoneInputRef && this.phoneInputRef.nativeElement;
    if (el) {
      el.blur();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/phone-input/phone-input.component.ts